import { useRef, useState } from "react";
import { Camera, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { MAX_AVATAR_DATA_URL_LENGTH } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { Avatar } from "@/components/Avatar";

type Props = {
  src?: string | null;
  name?: string | null;
  onChange: () => void;
  /** Foto quadrada grande com o lápis no canto (topo do Meu perfil), em vez do círculo. */
  quadrado?: boolean;
};

/** Avatar grande com overlay de upload no hover. Centralizado pra uso em hero. */
export function AvatarUpload({ src, name, onChange, quadrado }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const updateAvatar = trpc.me.updateAvatar.useMutation({
    onSuccess: () => {
      toast.success("Avatar atualizado");
      onChange();
    },
    onError: (err) => toast.error(err.message || "Falha ao atualizar avatar"),
    onSettled: () => setUploading(false),
  });

  const removeAvatar = trpc.me.removeAvatar.useMutation({
    onSuccess: () => {
      toast.success("Avatar removido");
      onChange();
    },
    onError: (err) => toast.error(err.message || "Falha ao remover avatar"),
  });

  const handleFile = (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    if (file.size > 500_000) {
      toast.error("Imagem muito grande (max ~500KB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      if (dataUrl.length > MAX_AVATAR_DATA_URL_LENGTH) {
        toast.error("Imagem muito grande apos codificacao");
        return;
      }
      setUploading(true);
      updateAvatar.mutate({ dataUrl });
    };
    reader.onerror = () => toast.error("Falha ao ler o arquivo");
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="group relative">
        {quadrado ? (
          src ? (
            <img src={src} alt={name ?? "Foto"} className="h-36 w-36 rounded-2xl object-cover shadow-md sm:h-44 sm:w-44" />
          ) : (
            <span className="flex h-36 w-36 items-center justify-center rounded-2xl bg-zinc-900 text-5xl font-semibold text-white shadow-md sm:h-44 sm:w-44">
              {(name ?? "?").trim().charAt(0).toUpperCase()}
            </span>
          )
        ) : (
          <Avatar src={src} name={name} size="xl" className="ring-4 ring-white shadow-lg" />
        )}

        {quadrado ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-2 -right-2 flex h-10 w-10 items-center justify-center rounded-full border border-zinc-200 bg-white text-brand-700 shadow-md transition-colors hover:bg-zinc-50 disabled:cursor-wait dark:border-border dark:bg-card dark:text-primary"
            aria-label="Trocar foto"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 text-transparent transition-all hover:bg-black/50 hover:text-white disabled:cursor-wait"
            aria-label="Trocar foto"
          >
            {uploading ? (
              <Loader2 className="h-6 w-6 animate-spin" />
            ) : (
              <Camera className="h-6 w-6" />
            )}
          </button>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = "";
          }}
        />
      </div>

      {src && (
        <button
          type="button"
          onClick={() => removeAvatar.mutate()}
          disabled={removeAvatar.isPending}
          className="inline-flex items-center gap-1 text-xs text-zinc-500 transition-colors hover:text-red-600"
        >
          <Trash2 className="h-3 w-3" />
          Remover foto
        </button>
      )}
    </div>
  );
}
